// src/components/admin/rpa/buttons/AnalyzeSOCButton.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const POLL_MS = 3000;        // More frequent polling
const MAX_MINUTES = 20;      // stop polling after this many minutes

const AnalyzeSOCButton = ({ docId, disabledHint }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  // analysis result and/or status
  const [result, setResult] = useState(null);      // final analysis payload (once done)
  const [err, setErr] = useState('');

  // progress & status
  const [phase, setPhase] = useState('idle');      // idle | queued | working | done | error
  const [queuedMsg, setQueuedMsg] = useState('');  // backend message on 202
  const [elapsedSec, setElapsedSec] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const timerRef = useRef(null);
  const tickRef = useRef(null);
  const pollCountRef = useRef(0);

  // --- helpers --------------------------------------------------------------

  const stopTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    pollCountRef.current = 0;
  };

  const closePanel = () => {
    stopTimers();
    setOpen(false);
    // Reset state when closing
    setPhase('idle');
    setResult(null);
    setErr('');
    setElapsedSec(0);
  };

  // Enhanced document fetching with multiple strategies
  const fetchDocMeta = async () => {
    const timestamp = Date.now();
    pollCountRef.current += 1;
    
    console.log(`[SOC] Poll #${pollCountRef.current} for docId: ${docId}`);
    
    try {
      // Strategy 1: Force fresh data with multiple cache-busting techniques
      const res = await axios.get(`${api}/admin/rpa/docs`, {
        params: { 
          _t: timestamp,
          _r: Math.random(),
          docId: docId // Specific doc filter if supported
        },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'If-None-Match': '*' // Prevent 304 responses
        },
        withCredentials: true
      });
      
      console.log(`[SOC] Poll response status: ${res.status}`);
      
      const docs = Array.isArray(res?.data?.items) ? res.data.items : 
                   Array.isArray(res?.data?.docs) ? res.data.docs : 
                   Array.isArray(res?.data) ? res.data : [];
      
      const doc = docs.find(d => String(d._id) === String(docId));
      
      if (doc) {
        console.log(`[SOC] Found doc:`, {
          _id: doc._id,
          socFindings: !!doc.socFindings,
          socAnalysisQueuedAt: doc.socAnalysisQueuedAt,
          lastAnalyzedAt: doc.lastAnalyzedAt,
          workflowAssignment: doc.workflowAssignment ? {
            templateId: doc.workflowAssignment.templateId,
            steps: doc.workflowAssignment.steps?.map(s => ({ id: s.id, status: s.status }))
          } : null
        });
      } else {
        console.warn(`[SOC] Document ${docId} not found in response`);
      }
      
      return doc || null;
    } catch (error) {
      console.error(`[SOC] Poll failed:`, error);
      return null;
    }
  };

  // Alternative strategy: Direct endpoint check
  const checkAnalysisStatus = async () => {
    try {
      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/analyze/soc`,
        {},
        { 
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache'
          }
        }
      );
      
      console.log(`[SOC] Direct check status: ${res.status}`);
      
      // If we get 200, analysis is complete
      if (res.status === 200) {
        return { completed: true, result: res.data };
      }
      
      return { completed: false, result: null };
    } catch (error) {
      console.warn(`[SOC] Direct check failed:`, error.message);
      return { completed: false, result: null };
    }
  };

  // Polling loop when queued
  const beginPolling = () => {
    console.log('[SOC] Starting enhanced polling for docId:', docId);
    pollCountRef.current = 0;
    
    // elapsed timer
    tickRef.current = setInterval(() => {
      setElapsedSec(sec => sec + 1);
    }, 1000);

    // status poller with dual strategy
    timerRef.current = setInterval(async () => {
      try {
        // Strategy 1: Check via document list
        const doc = await fetchDocMeta();
        
        if (doc) {
          // Primary completion check: socFindings exists AND not queued
          if (doc.socFindings && !doc.socAnalysisQueuedAt) {
            console.log('[SOC] ✅ Completion detected via document list');
            stopTimers();
            setPhase('done');
            setResult({
              success: true,
              analysis: { azure: true, raw: null, findings: doc.socFindings },
            });
            return;
          }
          
          // Secondary completion check: workflow step completed
          if (doc.workflowAssignment && doc.workflowAssignment.steps) {
            const socStep = doc.workflowAssignment.steps.find(s => s.id === 'analyze_soc');
            if (socStep && socStep.status === 'completed') {
              console.log('[SOC] ✅ Completion detected via workflow step');
              
              // Try to get findings
              const statusCheck = await checkAnalysisStatus();
              if (statusCheck.completed) {
                stopTimers();
                setPhase('done');
                setResult(statusCheck.result);
                return;
              }
            }
          }
          
          // Still working - update UI hints
          if (doc.socAnalysisQueuedAt) {
            setPhase('working');
            setLastUpdatedAt(doc.updatedAt || doc.createdAt || null);
          }
        }
        
        // Strategy 2: Every 5th poll, try direct endpoint check
        if (pollCountRef.current % 5 === 0) {
          console.log('[SOC] Trying direct endpoint check...');
          const statusCheck = await checkAnalysisStatus();
          if (statusCheck.completed) {
            console.log('[SOC] ✅ Completion detected via direct check');
            stopTimers();
            setPhase('done');
            setResult(statusCheck.result);
            return;
          }
        }

        // Safety stop after MAX_MINUTES
        if (elapsedSec >= MAX_MINUTES * 60) {
          console.warn('[SOC] Polling timeout reached');
          stopTimers();
          setPhase('error');
          setErr('Analysis timed out. The analysis may have completed - try refreshing the page or clicking Analyze SOC again.');
        }
      } catch (e) {
        console.warn('[SOC] Polling tick failed:', e?.message || e);
      }
    }, POLL_MS);
  };

  const run = async () => {
    if (!docId) return;
    setBusy(true);
    setErr('');
    setResult(null);
    setElapsedSec(0);
    setLastUpdatedAt(null);
    stopTimers();

    try {
      // kick off analysis
      console.log('[SOC] Starting analysis for docId:', docId);
      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/analyze/soc`,
        {},
        { withCredentials: true, validateStatus: () => true }
      );

      console.log('[SOC] Analyze response:', res.status, res.data);

      // 200 => analysis is already available (cached) or completed inline
      if (res.status === 200) {
        console.log('[SOC] Analysis already complete, showing results immediately');
        setResult(res.data || { message: 'No data returned' });
        setPhase('done');
        setOpen(true);
        return;
      }

      // 202 => queued; start polling
      if (res.status === 202) {
        console.log('[SOC] Analysis queued, starting polling');
        setPhase('queued');
        setQueuedMsg(res.data?.message || 'Queued for analysis...');
        setOpen(true);
        beginPolling();
        return;
      }

      // other => error
      console.error('[SOC] analyze SOC unexpected:', res.status, res.data);
      setErr(res.data?.message || 'Analysis failed.');
      setPhase('error');
      setOpen(true);
    } catch (e) {
      console.error('[SOC] analyze SOC failed:', e);
      setErr('Analysis failed.');
      setPhase('error');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  // Clean up timers if component unmounts or panel closes
  useEffect(() => {
    return () => stopTimers();
  }, []);

  // --- render helpers -------------------------------------------------------

  const renderSourceLabel = () => {
    if (!result) return null;

    // If Azure Gov AI responded
    if (result.analysis?.azure) {
      return (
        <p style={{ color: 'green', fontWeight: 'bold', marginBottom: 6 }}>
          ✅ AI Analysis (Azure Gov)
        </p>
      );
    }

    // Otherwise fallback heuristic
    return (
      <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 6 }}>
        ⚠️ Heuristic Analysis (Fallback)
      </p>
    );
  };

  const renderStatus = () => {
    if (phase === 'queued') {
      return (
        <div style={statusBox}>
          <strong>Queued</strong>
          <div style={{ fontSize: 13, color: '#555' }}>{queuedMsg}</div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              Waiting for worker… {formatElapsed(elapsedSec)} (Poll #{pollCountRef.current})
            </span>
          </div>
        </div>
      );
    }

    if (phase === 'working') {
      return (
        <div style={statusBox}>
          <strong>Analyzing pages…</strong>
          <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
            Processing document with Azure AI. This may take several minutes.
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              Elapsed {formatElapsed(elapsedSec)} • Poll #{pollCountRef.current}
              {lastUpdatedAt ? ` • Last update ${new Date(lastUpdatedAt).toLocaleTimeString()}` : ''}
            </span>
          </div>
          <ProgressBar />
          <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
            Using dual-strategy polling to detect completion
          </div>
        </div>
      );
    }

    if (phase === 'error') {
      return (
        <div style={{ ...statusBox, borderColor: '#f87171', background: '#fef2f2' }}>
          <p style={{ color: 'crimson', margin: 0 }}>{err || 'Something went wrong.'}</p>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <button
        onClick={run}
        disabled={busy || !!disabledHint}
        title={disabledHint ? 'This file type may not be supported for SOC parsing.' : undefined}
      >
        {busy ? 'Analyzing…' : 'Analyze SOC'}
      </button>

      {open && (
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>SOC Analysis</strong>
            <button onClick={closePanel} style={{ padding: '2px 8px' }}>Close</button>
          </div>

          {renderStatus()}

          {/* Show the final result once ready */}
          {phase === 'done' && !err && result && (
            <>
              {renderSourceLabel()}
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0, maxHeight: 400, overflow: 'auto' }}>
                {typeof result === 'string'
                  ? result
                  : JSON.stringify(result.analysis, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------- small UI bits ---------------------------- */

const panel = {
  marginTop: 8,
  background: '#f8f9fb',
  border: '1px solid #e2e5ea',
  padding: 10,
  borderRadius: 8,
  maxWidth: 640
};

const statusBox = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 10,
  borderRadius: 8,
  marginBottom: 10
};

const Spinner = () => (
  <span
    style={{
      width: 14,
      height: 14,
      border: '2px solid #cbd3e1',
      borderTop: '2px solid #5b7cff',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'spin 1s linear infinite'
    }}
  />
);

// simple indeterminate progress bar
const ProgressBar = () => (
  <div style={{ marginTop: 8, height: 8, background: '#e9edf5', borderRadius: 6, overflow: 'hidden' }}>
    <div style={{
      width: '35%',
      height: '100%',
      background: '#5b7cff',
      animation: 'bar 1.4s ease-in-out infinite'
    }} />
    <style>{`
      @keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
      @keyframes bar {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(30%); }
        100% { transform: translateX(120%); }
      }
    `}</style>
  </div>
);

function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default AnalyzeSOCButton;
